/**
 * Enquiry forms — the four doors, one implementation.
 *
 * Commission, repair, cash-for-gold and appointment all post to the same app-proxy
 * route. Their FIELDS differ and their behaviour does not, so the behaviour is here and
 * nowhere else. The theme has paid for the alternative once already: the $0.00 price bug
 * came from the same markup living in three files and only one of them getting fixed.
 *
 * PROGRESSIVE ENHANCEMENT, NOT A JS FORM. The form element already posts natively — see
 * snippets/djh-enquiry-fields.liquid. This intercepts to answer inline. If this file
 * fails to load, is blocked, or throws, the browser posts the form the ordinary way and
 * the route answers. Nothing here is load-bearing for the enquiry reaching the shop.
 *
 * 🔴 THE ONE RULE: NEVER SHOW SUCCESS ON A FAILED WRITE.
 *
 * Shopify's contact form always succeeded from the customer's side, so nobody had to
 * think about this. Ours can genuinely fail, and "thank you, we'll be in touch" over a
 * failed write is worse than the inbox it replaced — the customer stops waiting, and the
 * shop never learns there was anything to wait for. Every branch below either records the
 * enquiry or says plainly that it did not.
 *
 * FOUR FAILURES, FOUR MESSAGES, and they are different on purpose. "Something went wrong"
 * tells a customer nothing about what to do next:
 *
 *     refused      a required detail is missing (D-139: a name and a way to reply).
 *                  The customer can fix this. Say which.
 *     rate limited too many in a short window. Not their fault, and it clears.
 *     server       our end broke. Give them the phone number — the shop is reachable
 *                  even when the website is not.
 *     network      the request never arrived. Distinct from a server error because a
 *                  retry is much more likely to work, and this is the one that actually
 *                  happens most.
 */
(() => {
  const forms = document.querySelectorAll('[data-djh-enquiry-form]');
  if (!forms.length) return;

  /**
   * Copy read from the DOM, never hardcoded here. Every string a customer sees is a
   * locale string set in Liquid — this file has no opinion about wording, which is what
   * lets Henry change it without a deploy (D-71: the copy is his).
   */
  const copy = (form, key, fallback) => {
    const el = form.querySelector('[data-djh-enquiry-copy]');
    if (!el) return fallback;
    return el.dataset[key] || fallback;
  };

  const show = (el, message) => {
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  };

  const clear = (ok, err) => {
    show(ok, '');
    show(err, '');
  };

  forms.forEach((form) => {
    // Bound per FORM, not with a module-level flag. The script tag sits in each section,
    // so a page carrying two doors would run this twice — and a global guard would then
    // bind the first form and silently leave the second posting natively while looking
    // identical. A marker on the element is the only version that is right in both cases.
    if (form.dataset.djhEnquiryBound) return;
    form.dataset.djhEnquiryBound = '1';

    const ok = form.querySelector('[data-djh-enquiry-ok]');
    const err = form.querySelector('[data-djh-enquiry-error]');
    const button = form.querySelector('button[type="submit"]');
    const buttonLabel = button ? button.textContent : '';

    form.addEventListener('submit', async (event) => {
      // Only take over once we know we can answer. If fetch is missing the listener
      // returns without preventDefault and the native post happens — the same path a
      // customer with no scripting takes.
      if (typeof window.fetch !== 'function') return;
      event.preventDefault();
      clear(ok, err);

      if (button) {
        button.disabled = true;
        button.textContent = copy(form, 'sending', buttonLabel);
      }

      const done = (message, isError) => {
        if (button) {
          button.disabled = false;
          button.textContent = buttonLabel;
        }
        show(isError ? err : ok, message);
        // Move focus to what was just said. A message that appears below a button the
        // customer is still looking at is a message a screen-reader user has to go
        // hunting for, and an error nobody sees is the failure this file exists to stop.
        const target = isError ? err : ok;
        if (target) {
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: false });
        }
      };

      // JSON, because that is what the route reads. The form element still declares a
      // native POST for the no-script path — see the note at the foot of this file about
      // the content type those two disagree on.
      const payload = {};
      new FormData(form).forEach((value, key) => {
        payload[key] = typeof value === 'string' ? value : '';
      });

      let res;
      try {
        res = await fetch(form.action, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
      } catch (_networkErr) {
        // The request never arrived. Deliberately NOT folded in with a server error:
        // a retry after a network failure usually works, and after a 500 it usually
        // does not, so a customer told the wrong one wastes their time or gives up.
        done(copy(form, 'errorNetwork', 'That did not reach us. Please try again.'), true);
        return;
      }

      let body = null;
      try {
        body = await res.json();
      } catch (_parseErr) {
        body = null;
      }

      if (res.ok) {
        // 🔴 A 200 IS NOT A RECEIPT. `delivered: true` IS (D-359, 2026-10-24).
        //
        // The route answers a tripped honeypot with 200 and the same shape as success
        // but WITHOUT `delivered` — deliberately, so a bot learns nothing about which field
        // it caught on — and answers 200 with `delivered: false` in an environment that may
        // not mail the shop. A 200 alone therefore cannot tell us whether the shop got the
        // message, and treating it as success is the failure this whole file exists to
        // prevent: a customer told "thank you" over a message nobody received.
        //
        // This read `id` until D-359. The route no longer writes a record, so it no longer
        // returns one, and every real enquiry was shown "not sent" after the shop's email
        // had gone out (found on the first test enquiries, 2026-10-24).
        //
        // The honeypot is not theoretical: a password manager filling a hidden field is the
        // ordinary way a real person trips it, which is why the field is autocomplete="off".
        if (body && body.ok === true && body.delivered === true) {
          form.reset();
          done(copy(form, 'success', 'Thank you — we have your request.'), false);
        } else {
          done(copy(form, 'errorServer', 'Your message was not sent. Please call the shop.'), true);
        }
        return;
      }

      // `reason` is machine-readable and separate from the human `error`, so the branch
      // is chosen by a code rather than by matching prose. Prose gets reworded; a code
      // that changes breaks loudly, which is the right way round.
      const reason = (body && body.reason) || '';

      if (reason === 'rate_limited' || res.status === 429) {
        done(copy(form, 'errorRate', 'That is a lot of requests. Please try again shortly.'), true);
        return;
      }

      if (reason === 'incomplete' || reason === 'unknown_kind' || reason === 'unreadable' || res.status === 400 || res.status === 422) {
        // The route is the authority on D-139 — it enforces, this markup only asks.
        // Prefer its own words about which detail is missing over our general line.
        const detail = (body && (body.error || body.message)) || '';
        done(detail || copy(form, 'errorRefused', 'Please check the required fields.'), true);
        return;
      }

      done(copy(form, 'errorServer', 'Something broke at our end. Please call the shop.'), true);
    });
  });
})();

/**
 * ⚠️ OPEN WITH OPS — THE NO-SCRIPT PATH AND THE CONTENT TYPE DISAGREE.
 *
 * The form element declares a native POST so the enquiry still reaches the shop with
 * scripting off; that path sends application/x-www-form-urlencoded. The route reads
 * application/json. So a customer without JavaScript most likely meets `400 unreadable`.
 *
 * That is VISIBLE rather than silent — the browser shows the response, so nobody is told
 * "thank you" over nothing, and the one rule holds. But it is a poor answer, and the fix
 * is small on their side: accept form-encoded as well as JSON. Raised rather than worked
 * around, because the alternatives are both worse — dropping the native POST would make
 * this file load-bearing for the enquiry arriving at all, and posting twice would be the
 * two-writer shape the spec rejected.
 */
